import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { T } from '@tolgee/react';
import Flag from 'react-world-flags';
import {
	TrackingDeliveryMode,
	TrackingLocation,
	TrackingLocationStatus,
	TrackingStepKey,
	TrackingTruck,
} from '../orderTracking.interface';
import {
	formatTime,
	formatDay,
	stageStatusFromTrucks,
	stepCompleted,
	stepCompletedAt,
} from '../orderTracking.utils';
import { Checkmark } from '../../../../assets/Checkmark';
import { ChevronDownIcon } from '../../../../assets/ChevronDownIcon';
import { TRACKING_DRAWER_TRANSITION } from '../orderTracking.constants';
import { cn } from '../../../../lib/utils';

interface RouteSectionProps {
	pickup: TrackingLocation;
	delivery: TrackingLocation;
	deliveryMode: TrackingDeliveryMode;
	trucks: TrackingTruck[];
}

const statusLabel = (status: TrackingLocationStatus) => {
	switch (status) {
		case 'completed':
			return <T keyName="orderTracking.locationCompleted">completed</T>;
		case 'in_progress':
			return <T keyName="orderTracking.locationInProgress">in progress</T>;
		default:
			return <T keyName="orderTracking.locationScheduled">scheduled</T>;
	}
};

const RoutePoint: React.FC<{
	location: TrackingLocation;
	label: React.ReactNode;
	dotClassName: string;
	last?: boolean;
}> = ({ location, label, dotClassName, last = false }) => {
	const window = [
		formatDay(location.date),
		location.timeFrom && location.timeTo
			? `${location.timeFrom} – ${location.timeTo}`
			: location.timeFrom,
	]
		.filter(Boolean)
		.join(' · ');

	return (
		<>
			<div className="flex flex-col items-center">
				<span
					className={cn(
						'w-4 h-4 mt-[2px] rounded-full border-[3px] border-solid border-white shadow-[0_0_0_1px_#E8E8EC] box-border flex-none',
						dotClassName,
					)}
				/>
				{!last ? <span className="flex-1 w-[2px] bg-track-line my-1" /> : null}
			</div>
			<div className={cn('min-w-0', !last && 'pb-3')}>
				<div className="flex gap-[6px] text-[12.5px] leading-[1.4] whitespace-nowrap overflow-hidden text-ellipsis">
					<span className="text-track-muted">{label}</span>
					<span className="text-track-slate font-medium">
						{window}
						{window ? ' · ' : ''}
						{statusLabel(location.status)}
					</span>
				</div>
				<div className="text-sm font-medium leading-[1.4] flex items-center gap-2 mt-[2px] text-track-ink">
					{location.countryCode ? (
						<span className="w-5 h-[14px] rounded-sm flex-none overflow-hidden shadow-[inset_0_0_0_1px_rgba(31,31,39,0.08)]">
							<Flag
								code={location.countryCode}
								width={20}
								height={14}
								className="object-cover block"
							/>
						</span>
					) : null}
					<span className="min-w-0">{location.address}</span>
				</div>
			</div>
		</>
	);
};

interface TruckStageRowsProps {
	trucks: TrackingTruck[];
	stepKey: TrackingStepKey;
	doneLabelKey: string;
	doneLabel: string;
	pendingLabelKey: string;
	pendingLabel: string;
	withLine: boolean;
	open: boolean;
}

const TruckStageRows: React.FC<TruckStageRowsProps> = ({
	trucks,
	stepKey,
	doneLabelKey,
	doneLabel,
	pendingLabelKey,
	pendingLabel,
	withLine,
	open,
}) => (
	<div className="col-span-2 grid grid-cols-[16px,minmax(0,1fr)] gap-x-3">
		<div className="flex flex-col items-center">
			{withLine ? <span className="flex-1 w-[2px] bg-track-line" /> : null}
		</div>
		<AnimatePresence initial={false}>
			{open ? (
				<motion.div
					key="rows"
					initial={{ height: 0, opacity: 0 }}
					animate={{ height: 'auto', opacity: 1 }}
					exit={{ height: 0, opacity: 0 }}
					transition={TRACKING_DRAWER_TRANSITION}
					className="min-w-0 overflow-hidden"
				>
					<div
						className={cn('flex flex-col gap-[6px] pb-3', !withLine && 'pt-3')}
					>
						{trucks.map((truck) => {
							const done = stepCompleted(truck, stepKey);
							const doneAt = stepCompletedAt(truck, stepKey);

							return (
								<div
									key={truck.id}
									className="flex items-center justify-between gap-3 text-[12.5px] leading-[1.4]"
								>
									<div className="flex items-center gap-2 min-w-0">
										{done ? (
											<span className="w-4 h-4 rounded-full bg-track-teal grid place-items-center flex-none">
												<Checkmark color="#FFFFFF" width={8} height={6} />
											</span>
										) : (
											<span className="w-4 h-4 rounded-full bg-white border-2 border-solid border-track-line box-border flex-none" />
										)}
										<span className="font-semibold text-track-ink whitespace-nowrap">
											<T
												keyName="orderTracking.truckNumber"
												params={{ number: truck.number }}
											>
												{'Truck #{number}'}
											</T>
										</span>
										<span className="text-track-muted whitespace-nowrap overflow-hidden text-ellipsis">
											{truck.plate}
										</span>
									</div>
									<span
										className={cn(
											'flex-none whitespace-nowrap font-medium',
											done ? 'text-track-slate' : 'text-track-muted',
										)}
									>
										{done ? (
											<>
												<T keyName={doneLabelKey}>{doneLabel}</T>
												{doneAt ? ` · ${formatTime(doneAt)}` : ''}
											</>
										) : (
											<T keyName={pendingLabelKey}>{pendingLabel}</T>
										)}
									</span>
								</div>
							);
						})}
					</div>
				</motion.div>
			) : null}
		</AnimatePresence>
	</div>
);

const RouteSection: React.FC<RouteSectionProps> = ({
	pickup,
	delivery,
	deliveryMode,
	trucks,
}) => {
	const [open, setOpen] = useState<boolean>(false);
	const isMultiple = deliveryMode === 'multiple' && trucks.length > 1;
	const loadingStatus = isMultiple
		? stageStatusFromTrucks(trucks, 'loaded')
		: pickup.status;
	const unloadingStatus = isMultiple
		? stageStatusFromTrucks(trucks, 'unloaded')
		: delivery.status;
	const showLoadingRows = isMultiple;
	const showUnloadingRows = isMultiple && loadingStatus !== 'scheduled';
	const hasRows = showLoadingRows || showUnloadingRows;

	return (
		<div className="flex flex-col">
			<div className="flex items-center justify-between mb-[10px]">
				<h3 className="m-0 text-sm font-semibold text-track-ink">
					<T keyName="orderTracking.route">Route</T>
				</h3>
				{hasRows ? (
					<button
						type="button"
						onClick={() => setOpen((value) => !value)}
						className="flex items-center gap-[6px] border-none bg-transparent p-0 font-[inherit] text-[13px] font-semibold text-track-link cursor-pointer transition hover:text-track-link-hover"
					>
						{open ? (
							<T keyName="orderTracking.hideTrucks">Hide trucks</T>
						) : (
							<T
								keyName="orderTracking.showTrucks"
								params={{ count: trucks.length }}
							>
								{'Show trucks ({count})'}
							</T>
						)}
						<span
							className={cn(
								'flex transition-transform duration-300 ease-[cubic-bezier(0.2,0,0.2,1)]',
								open && 'rotate-180',
							)}
						>
							<ChevronDownIcon color="#5080F8" width={10} height={6} />
						</span>
					</button>
				) : null}
			</div>
			<div className="grid grid-cols-[16px,minmax(0,1fr)] gap-x-3">
				<RoutePoint
					location={{ ...pickup, status: loadingStatus }}
					label={<T keyName="orderTracking.loading">Loading:</T>}
					dotClassName="bg-track-teal"
				/>
				{showLoadingRows ? (
					<TruckStageRows
						trucks={trucks}
						stepKey="loaded"
						doneLabelKey="orderTracking.loadedLabel"
						doneLabel="Loaded"
						pendingLabelKey="orderTracking.awaitingLoading"
						pendingLabel="Awaiting loading"
						withLine
						open={open}
					/>
				) : null}
				<RoutePoint
					location={{ ...delivery, status: unloadingStatus }}
					label={<T keyName="orderTracking.unloading">Unloading:</T>}
					dotClassName="bg-track-indigo"
					last
				/>
				{showUnloadingRows ? (
					<TruckStageRows
						trucks={trucks}
						stepKey="unloaded"
						doneLabelKey="orderTracking.unloadedLabel"
						doneLabel="Unloaded"
						pendingLabelKey="orderTracking.awaitingUnloading"
						pendingLabel="Awaiting unloading"
						withLine={false}
						open={open}
					/>
				) : null}
			</div>
		</div>
	);
};

export default RouteSection;
